import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="skills-autocomplete"
export default class extends Controller {
  static targets = ["search", "results", "list"]
  static values = {
    url: { type: String, default: "/api/v1/skills" }
  }

  connect() {
    this.timeout = null
    this.nextIndex = Date.now()
    console.log('🎯 Skills autocomplete controller connected!')
    console.log('Search target exists:', this.hasSearchTarget)
    console.log('Results target exists:', this.hasResultsTarget)
    console.log('List target exists:', this.hasListTarget)

    // Show alert to confirm controller is loaded
    if (this.hasSearchTarget) {
      this.searchTarget.style.borderColor = 'green'
      setTimeout(() => {
        this.searchTarget.style.borderColor = ''
      }, 1000)
    }
  }

  search() {
    clearTimeout(this.timeout)

    const query = this.searchTarget.value.trim()
    console.log('Searching for skills:', query)

    if (query.length < 2) {
      this.hideResults()
      return
    }

    this.timeout = setTimeout(() => {
      console.log('Fetching skills for query:', query)
      this.fetchSkills(query)
    }, 300)
  }

  async fetchSkills(query) {
    try {
      const url = `${this.urlValue}?q=${encodeURIComponent(query)}`
      console.log('Fetching from URL:', url)

      const response = await fetch(url)
      console.log('Response status:', response.status)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const skills = await response.json()
      console.log('Skills received:', skills)
      this.displayResults(skills)
    } catch (error) {
      console.error('Error fetching skills:', error)
      this.hideResults()
    }
  }

  displayResults(skills) {
    if (skills.length === 0) {
      this.resultsTarget.innerHTML = '<div class="p-3 text-sm text-gray-500">No skills found</div>'
      this.resultsTarget.classList.remove('hidden')
      return
    }

    this.resultsTarget.innerHTML = skills.map(skill => `
      <div class="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0 transition-colors"
           data-action="click->skills-autocomplete#selectSkill"
           data-skill-id="${skill.id}"
           data-skill-name="${skill.name}"
           data-skill-industry="${skill.industry || ''}"
           data-skill-description="${skill.description || ''}">
        <div class="font-semibold text-sm text-gray-900">${this.escapeHtml(skill.name)}</div>
        <div class="text-xs text-gray-600 mt-0.5">${this.escapeHtml(skill.industry || '')}</div>
        ${skill.description ? `<div class="text-xs text-gray-500 mt-0.5">${this.escapeHtml(skill.description)}</div>` : ''}
      </div>
    `).join('')

    this.resultsTarget.classList.remove('hidden')
  }

  selectSkill(event) {
    const element = event.currentTarget
    const skillId = element.dataset.skillId
    const skillName = element.dataset.skillName
    const skillIndustry = element.dataset.skillIndustry
    const skillDescription = element.dataset.skillDescription

    // Check if skill already exists
    const existingSkills = Array.from(this.listTarget.querySelectorAll('input[name*="[skill_id]"]'))
    const alreadyExists = existingSkills.some(input => input.value === skillId)

    if (alreadyExists) {
      // Show brief feedback that skill already exists
      this.searchTarget.style.borderColor = 'orange'
      setTimeout(() => {
        this.searchTarget.style.borderColor = ''
      }, 1000)
      this.searchTarget.value = ''
      this.hideResults()
      return
    }

    this.addSkillToList(skillId, skillName, skillIndustry, skillDescription)
    this.searchTarget.value = ''
    this.hideResults()
  }

  addSkillToList(skillId, skillName, skillIndustry, skillDescription) {
    const index = this.nextIndex++

    // Remove "no skills" message if it exists
    const emptyMessage = this.listTarget.querySelector('.text-center')
    if (emptyMessage) {
      emptyMessage.remove()
    }

    const html = `
      <div class="relative inline-flex items-center px-3 py-1.5 bg-section-bg rounded border border-gray-200 transition-all hover:border-primary group"
           data-skill-item
           title="${this.escapeHtml(skillDescription)}">
        <input type="hidden"
               name="user[user_skills_attributes][${index}][skill_id]"
               value="${skillId}">
        <span class="text-sm text-text-primary">${this.escapeHtml(skillName)}</span>
        <button type="button"
                data-action="click->skills-autocomplete#removeSkill"
                class="ml-2 text-gray-400 hover:text-red-600 transition-colors">
          <i class="fas fa-times text-xs"></i>
        </button>
      </div>
    `

    this.listTarget.insertAdjacentHTML('beforeend', html)

    // Show success feedback
    this.searchTarget.style.borderColor = 'green'
    setTimeout(() => {
      this.searchTarget.style.borderColor = ''
    }, 1000)

    // Focus back on search
    this.searchTarget.focus()
  }

  removeSkill(event) {
    const item = event.currentTarget.closest('[data-skill-item]')

    // If this is an existing record, mark it for deletion instead of removing from DOM
    const destroyInput = item.querySelector('input[name$="[_destroy]"]')
    if (destroyInput) {
      destroyInput.value = "1"
      item.style.display = 'none'
    } else {
      // New record, safe to remove from DOM
      item.remove()
    }

    // Show empty message if no skills remain
    const remainingSkills = Array.from(this.listTarget.querySelectorAll('[data-skill-item]'))
      .filter(el => el.style.display !== 'none')
    
    if (remainingSkills.length === 0) {
      this.listTarget.innerHTML = `
        <div class="w-full text-center py-6 text-gray-500 text-sm">
          <i class="fas fa-lightbulb text-2xl mb-2 text-gray-400"></i>
          <p>No skills added yet.</p>
          <p class="text-xs mt-1">Search above to add skills to your profile.</p>
        </div>
      `
    }
  }

  hideResults() {
    this.resultsTarget.classList.add('hidden')
  }

  // Click outside to close results
  disconnect() {
    clearTimeout(this.timeout)
  }

  // Utility function to escape HTML and prevent XSS
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
